import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import { Loader2, FileText, Mail, Phone, MessageCircle, Download, Receipt, Pencil, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { nom: "", email: "", telephone: "", montant: "20 $US", description: "Frais de participation — Détection DREAM2REALITY" };

const editCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] w-full";

function EditFactureModal({ facture, onSaved, onClose }) {
  const [form, setForm] = useState({
    nom: facture.nom || "", email: facture.email || "", telephone: facture.telephone || "",
    montant: facture.montant || "", description: facture.description || "", conditions: facture.conditions || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/factures/${facture.id}`, {
        nom: form.nom, montant: form.montant, description: form.description, conditions: form.conditions,
        email: form.email || null, telephone: form.telephone || null,
      }, { withCredentials: true });
      toast.success(`Facture ${facture.numero} mise à jour — PDF régénéré`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Erreur lors de la mise à jour"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid={`facture-edit-modal-${facture.id.slice(0, 8)}`}>
      <div className="bg-[#121214] border border-[#C5A059]/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Modifier la facture {facture.numero}</p>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors" aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Nom du client</label>
              <input value={form.nom} onChange={set("nom")} data-testid="facture-edit-nom" className={`${editCls} mt-1`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Montant</label>
              <input value={form.montant} onChange={set("montant")} data-testid="facture-edit-montant" className={`${editCls} mt-1`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Email</label>
              <input value={form.email} onChange={set("email")} data-testid="facture-edit-email" className={`${editCls} mt-1`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Téléphone</label>
              <input value={form.telephone} onChange={set("telephone")} data-testid="facture-edit-telephone" className={`${editCls} mt-1`} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Description</label>
            <input value={form.description} onChange={set("description")} data-testid="facture-edit-description" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Contenu du contrat (conditions)</label>
            <textarea rows={10} value={form.conditions} onChange={set("conditions")} data-testid="facture-edit-conditions"
              className={`${editCls} mt-1 resize-y leading-relaxed`} />
          </div>
        </div>
        <div className="flex items-center gap-4 mt-6">
          <button onClick={save} disabled={saving} data-testid="facture-edit-save"
            className="bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer et régénérer le PDF"}
          </button>
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#71717A] hover:text-white transition-colors">Annuler</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminFactures() {
  const [factures, setFactures] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/factures`, { withCredentials: true }).then((r) => setFactures(r.data)).catch(() => toast.error("Erreur de chargement"));
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() && !form.telephone.trim()) {
      toast.error("Renseignez au moins un email ou un numéro de téléphone");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/factures`, {
        nom: form.nom, email: form.email || null, telephone: form.telephone || null,
        montant: form.montant, description: form.description,
      }, { withCredentials: true });
      setLastCreated(data);
      toast.success(`Facture ${data.numero} créée${data.envoye_email ? " et envoyée par email" : ""}`);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Erreur de création"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-factures" className="grid lg:grid-cols-2 gap-10">
      <form onSubmit={submit} className="border border-[#27272A] bg-[#121214] p-8 self-start" data-testid="facture-form">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">Nouvelle facture électronique</p>
        <p className="text-xs text-[#71717A] mb-6 leading-relaxed">
          La facture PDF contient le contrat avec les conditions acceptées lors de l'inscription. Elle est envoyée par email si un email est fourni, et un lien WhatsApp est généré si un numéro est fourni.
        </p>
        <div className="space-y-6">
          <input required value={form.nom} onChange={set("nom")} placeholder="Nom complet du client *" data-testid="facture-nom" className="input-editorial" />
          <input type="email" value={form.email} onChange={set("email")} placeholder="Email (envoi automatique de la facture)" data-testid="facture-email" className="input-editorial" />
          <input value={form.telephone} onChange={set("telephone")} placeholder="Téléphone WhatsApp (ex : +243 862 624 007)" data-testid="facture-telephone" className="input-editorial" />
          <div className="grid grid-cols-2 gap-6">
            <input value={form.montant} onChange={set("montant")} placeholder="Montant" data-testid="facture-montant" className="input-editorial" />
            <input value={form.description} onChange={set("description")} placeholder="Description" data-testid="facture-description" className="input-editorial" />
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="facture-submit"
          className="mt-8 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Receipt size={15} />}
          Générer et envoyer
        </button>

        {lastCreated && (
          <div className="mt-8 border border-[#C5A059]/40 bg-[#0A0A0C] p-5" data-testid="facture-last">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-3">Dernière facture créée — {lastCreated.numero}</p>
            <div className="flex flex-wrap gap-3">
              <a href={lastCreated.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="facture-last-pdf"
                className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-4 py-2.5 text-[10px] uppercase tracking-widest hover:bg-[#C5A059] hover:text-black transition-colors">
                <Download size={13} /> Voir le PDF
              </a>
              {lastCreated.whatsapp_url && (
                <a href={lastCreated.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="facture-last-whatsapp"
                  className="flex items-center gap-2 bg-[#25D366] text-black px-4 py-2.5 text-[10px] uppercase tracking-widest font-semibold hover:bg-[#3be07a] transition-colors">
                  <MessageCircle size={13} /> Envoyer via WhatsApp
                </a>
              )}
            </div>
            {lastCreated.envoye_email
              ? <p className="text-[11px] text-emerald-400 mt-3">Email envoyé à {lastCreated.email}</p>
              : lastCreated.email && <p className="text-[11px] text-amber-400 mt-3">Email non délivré à {lastCreated.email} — utilisez le lien PDF ou WhatsApp.</p>}
          </div>
        )}
      </form>

      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6">Factures émises ({factures.length})</p>
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {factures.length === 0 && <p className="px-5 py-10 text-center text-[#71717A] text-sm">Aucune facture pour le moment.</p>}
          {factures.map((f) => (
            <div key={f.id} data-testid={`facture-row-${f.id.slice(0, 8)}`} className="px-5 py-4 flex items-center gap-4">
              <FileText size={16} className="text-[#C5A059] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{f.nom} <span className="text-[#C5A059] text-xs">{f.numero}</span></p>
                <p className="text-xs text-[#71717A] flex flex-wrap gap-x-4 mt-0.5">
                  {f.email && <span className="flex items-center gap-1"><Mail size={11} /> {f.email} {f.envoye_email ? "· envoyée" : "· non délivrée"}</span>}
                  {f.telephone && <span className="flex items-center gap-1"><Phone size={11} /> {f.telephone}</span>}
                  <span>{new Date(f.created_at).toLocaleDateString("fr-FR")}</span>
                </p>
              </div>
              <span className="font-display font-bold text-[#C5A059] whitespace-nowrap">{f.montant}</span>
              <button onClick={() => setEditing(f)} data-testid={`facture-edit-${f.id.slice(0, 8)}`} title="Modifier le contenu (contrat, montant…) et régénérer le PDF"
                className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier la facture">
                <Pencil size={15} />
              </button>
              <a href={f.pdf_url} target="_blank" rel="noopener noreferrer" data-testid={`facture-pdf-${f.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Ouvrir le PDF">
                <Download size={15} />
              </a>
            </div>
          ))}
        </div>
      </div>

      {editing && <EditFactureModal facture={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
    </div>
  );
}
